import { Component, type ReactNode } from 'react';

interface Props {
  children?: ReactNode;
  fallback: ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * Allows you render a fallback component if you have a child that throws.
 *
 * For example:
 *
 * const MyQueryComponent = () => {
 *   const { data } = useSuspenseQuery( ... );  <-- this hook can throw
 *
 *   if (!data) {
 *     return <div>!Empty</div>;
 *   }
 *
 *   return <div>{data}</div>;
 * }
 *
 * <QueryErrorBoundary fallback={<div role="alert">!Error<div}>
 *   <Suspense fallback={<div>!Loading</div>}>
 *      <MyQueryComponent />
 *   </Suspence>
 * </QueryErrorBoundary>
 *
 * @see https://www.apollographql.com/docs/react/data/suspense#error-handling
 */
export class QueryErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
  };

  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(_error: Error): State {
    return { hasError: true };
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }

    return this.props.children;
  }
}
